import type { BarSeriesOption, EChartsOption } from 'echarts';

import {
  FONT_SIZE,
  FormatMetric,
  formatAxisValue,
  formatValueLabel,
  toBaseOption,
  toTooltipHtml,
  toTooltipStyle,
  toValueLabelStyle,
} from './shared';

// Where each step's bar sits: from the lower of the running totals before
// and after it, as tall as its change. The total runs from zero.
export const toWaterfallBars = (
  steps: StudioCharts.WaterfallStep[],
): { base: number; height: number }[] => {
  let runningTotal = 0;
  return steps.map((step) => {
    if (step.isTotal) {
      return { base: Math.min(0, step.value), height: Math.abs(step.value) };
    }
    const before = runningTotal;
    runningTotal += step.value;
    return {
      base: Math.min(before, runningTotal),
      height: Math.abs(step.value),
    };
  });
};

// Stacked on its base, whatever its sign (see toWaterfallBars)
const STACK = 'waterfall';

// A waterfall, as the Highcharts one draws it: each term a step up (in the
// first colour) or down (in the second) from the running total, then the
// total from zero. Its bars stand on hidden bases, which ignore the pointer.
const toWaterfallOption = ({
  model,
  theme,
  formatMetric,
}: {
  model: StudioCharts.WaterfallModel;
  theme: StudioCharts.Theme;
  formatMetric: FormatMetric;
}): EChartsOption => {
  const bars = toWaterfallBars(model.steps);
  const base: BarSeriesOption = {
    type: 'bar',
    id: `${model.seriesId}:base`,
    stack: STACK,
    stackStrategy: 'all',
    silent: true,
    data: bars.map((bar) => bar.base),
    itemStyle: { opacity: 0 },
    emphasis: { disabled: true },
    tooltip: { show: false },
  };
  const steps: BarSeriesOption = {
    type: 'bar',
    id: model.seriesId,
    name: model.name,
    stack: STACK,
    stackStrategy: 'all',
    data: model.steps.map((step, index) => {
      const isDeselected =
        !!model.selectedKeys &&
        step.key !== undefined &&
        !model.selectedKeys.includes(step.key);
      return {
        value: bars[index].height,
        itemStyle: {
          color: isDeselected
            ? theme.deselected
            : step.value < 0
              ? model.downColor
              : model.upColor,
          borderRadius: 2,
        },
      };
    }),
    label: model.showValues
      ? {
          show: true,
          position: 'top',
          ...toValueLabelStyle(theme),
          // The step's change, not its bar's height
          formatter: ({ dataIndex }) =>
            formatValueLabel({
              metricId: model.metricId,
              value: model.steps[dataIndex].value,
              formatMetric,
            }),
        }
      : undefined,
  };
  return {
    ...toBaseOption(theme),
    grid: {
      left: 8,
      right: 12,
      top: 24,
      bottom: 8,
      outerBoundsMode: 'same',
      outerBoundsContain: 'axisLabel',
    },
    xAxis: {
      type: 'category',
      data: model.steps.map((step) => step.label),
      axisLine: { lineStyle: { color: theme.axisLine } },
      axisTick: { show: false },
      axisLabel: {
        color: theme.textPrimary,
        fontSize: FONT_SIZE,
        hideOverlap: true,
      },
    },
    yAxis: {
      type: 'value',
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: theme.textMuted,
        fontSize: FONT_SIZE,
        formatter: formatAxisValue,
        hideOverlap: true,
      },
      splitLine: { lineStyle: { color: theme.gridLine } },
    },
    tooltip: {
      ...toTooltipStyle(theme),
      trigger: 'item',
      formatter: (params) => {
        const item = Array.isArray(params) ? params[0] : params;
        const step =
          item && typeof item.dataIndex === 'number'
            ? model.steps[item.dataIndex]
            : undefined;
        if (!step) {
          return '';
        }
        return toTooltipHtml({
          title: step.label,
          rows: [
            {
              marker: typeof item.marker === 'string' ? item.marker : '',
              name: model.name,
              value: formatMetric({
                metricId: model.metricId,
                value: step.value,
              }),
            },
          ],
        });
      },
    },
    series: [base, steps],
  };
};

export default toWaterfallOption;
